import { useEffect, useRef, useCallback } from 'react';
import { auth, onAuthStateChanged, onIdTokenChanged, signInWithCustomToken } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useApolloClient } from '@apollo/client/react';
import {
  ME_QUERY,
  GET_COMPANY_INFO,
  TRIGGER_TRIAL_START_EVENT,
  type MeQueryResult,
  type GetCompanyInfoQueryResult,
} from '@scio3/graphql-client';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import type { CompanyInfo } from '@scio3/shared-types';
import {
  isInIframeContext,
  notifyReady,
  requestCustomToken,
  notifyLoggedIn,
  startParentListener,
} from '@/lib/iframe-child-bridge';
import {
  analyticsLogin,
  analyticsLoginResume,
  analyticsTrialCreated,
  analyticsIdentify,
} from '@/lib/analytics-service';
import { checkSamlSessionExpired } from '@/lib/saml-session';
import { handleDualLiveRouting } from '@/lib/sso-cookies';
import { fullLogout } from '@/lib/logout';
import { useFavoritesStore } from '@/stores/favorites-store';
import { getCacheOwner, setCacheOwnerStorage } from '@/lib/cache-owner';
import { clearPersistedPaths } from '@/hooks/usePersistedPath';
import {
  markHomeLandPending,
  clearHomeLandPending,
  hasHomeLandedThisSession,
  markHomeLandedThisSession,
} from '@/hooks/useAutoLandFolder';
import { NOT_TRIGGERED } from '@/features/get-started/constants';
import { clearPersistedCache } from '@scio3/graphql-client';
import { reportAuthErrorIfSystemic, reportAppError } from '@/lib/telemetry/faro';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const setAuth = useAuthStore((s) => s.setAuth);
  const setMe = useAuthStore((s) => s.setMe);
  const setCompany = useAuthStore((s) => s.setCompany);
  const setCompanyLoaded = useAuthStore((s) => s.setCompanyLoaded);
  const setLoading = useAuthStore((s) => s.setLoading);
  const setTeams = useTeamStore((s) => s.setTeams);
  const client = useApolloClient();
  const embeddedAuthStarted = useRef(false);
  const loginTracked = useRef(false);
  // Per-session guard for the trialCreated GTM event. Backend gates the
  // first fire via `company.payment.trialStartEventFired`; this ref
  // prevents a double-fire if AuthProvider re-runs the bootstrap path
  // within a single page life (e.g. ME_QUERY refetch on window focus).
  const trialEventTracked = useRef(false);
  // Group Security v3 (§12, D8) — the user id we last set up login home-land for. Keyed by user so a
  // new sign-in in the same tab re-fires, while a focus/WS ME_QUERY refetch (same user) does not.
  const homeLandUserRef = useRef<string | null>(null);

  // Check if error contains INVALID_TEAM
  const hasInvalidTeamError = useCallback((result: { error?: { message?: string } }): boolean => {
    return result.error?.message?.includes('INVALID_TEAM') ?? false;
  }, []);

  // Check if user has no teams
  const hasNoTeams = useCallback((meData: MeQueryResult['me'] | null | undefined): boolean => {
    if (!meData) return false;
    return !meData.teams || meData.teams.length === 0;
  }, []);

  // Force logout: clear all auth state and sign out from Firebase
  const forceLogout = useCallback(async () => {
    await fullLogout(client);
  }, [client]);

  // Iframe child auth: request custom token from SCIO 1.0 parent.
  // Only attempts handshake if we detect we're in an iframe (isInIframeContext).
  // Sets auth-store.isEmbedded = true only after confirmed successful handshake.
  // Skip on /auth-microservice — that route is the Control Center bridge (parent is
  // smallapp-data-integration, not scio1). AuthMicroservicePage owns that protocol;
  // running createFirebaseCustomToken here times out and floods the console.
  useEffect(() => {
    if (!isInIframeContext) return;
    if (window.location.pathname.startsWith('/auth-microservice')) return;
    if (embeddedAuthStarted.current) return;
    embeddedAuthStarted.current = true;

    // Start listening for responsePromise messages before sending requests
    const cleanup = startParentListener({});

    async function authenticateViaParent() {
      try {
        // 1. Tell parent we're ready
        notifyReady();

        // 2. Request custom token from parent (SCIO 1.0 calls Meteor server)
        const { token } = await requestCustomToken();

        // 3. Sign in with custom token — triggers onAuthStateChanged below
        await signInWithCustomToken(auth, token);

        // 4. Handshake confirmed — mark as embedded (enables header hiding, etc.)
        useAuthStore.getState().setEmbedded(true);

        // 5. Tell parent auth is done
        notifyLoggedIn();
      } catch (err) {
        reportAuthErrorIfSystemic('custom-token', err);
        // Handshake failed (timeout, no parent, IDE preview) — isEmbedded stays false.
        // App continues as standalone with normal login flow.
        console.error('Embedded auth failed:', err);
      }
    }

    authenticateViaParent();

    return cleanup;
  }, []);

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // SAML sliding window: validate TTL, refresh on valid, sign out on expired
        if (checkSamlSessionExpired(firebaseUser.uid)) {
          await fullLogout(client);
          return;
        }

        // Cross-user cache isolation: if a different user logged in (e.g. logout
        // happened in scio1 without calling scio3's fullLogout), clear stale cache
        // before loading this user's data. Prevents #638 data leakage.
        // Skip on /share/ paths — clearStore crashes share page queries in-flight
        // (e.g. user signs in on another tab, then reloads share page).
        const storedOwner = getCacheOwner();
        if (
          storedOwner &&
          storedOwner.uid !== firebaseUser.uid &&
          !window.location.pathname.startsWith('/share/')
        ) {
          await client.clearStore();
          await clearPersistedCache();
          clearPersistedPaths();
          // Favorites are per-user; reset here too (this path bypasses fullLogout, and the
          // later setActiveTeam won't reset if the new user shares the same team).
          useFavoritesStore.getState().reset();
        }

        // Clear explicit-logout flag now that a new user has authenticated —
        // prevents stale flag from affecting future session-expiry redirects.
        sessionStorage.removeItem('scio3_explicit_logout');

        const token = await firebaseUser.getIdToken();
        setAuth({ firebaseUser, token });
      } else {
        // Only full-logout if the user WAS previously authenticated (genuine sign-out or session expiry).
        // When token is already null, the user was never logged in (e.g. initial load on /verify or
        // /password/reset) — just mark loading as settled without calling fullLogout().
        // This prevents a Safari cross-tab auth sync issue: Safari may fire onAuthStateChanged(null)
        // briefly during cross-tab login, and fullLogout()'s signOut(auth) call would actively
        // destroy the incoming auth state before it arrives.
        if (useAuthStore.getState().token) {
          // Share pages use anonymous Firebase auth — onAuthStateChanged(null) fires
          // transiently on reload before signInAnonymously completes. Skip fullLogout
          // to avoid clearing Apollo store while share queries are in flight.
          if (window.location.pathname.startsWith('/share/')) {
            setLoading(false);
          } else {
            await fullLogout(client);
          }
        } else {
          // No prior auth — just settle loading state (e.g. unauthenticated user on /verify page)
          setLoading(false);
        }
      }
    });
    return unsubscribe;
  }, [setLoading, setAuth, client]);

  // Auto-refresh token
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        // Skip stale token updates after logout — clearAuth sets firebaseUser to null,
        // but Firebase callbacks queued before signOut may still fire.
        if (!useAuthStore.getState().firebaseUser) return;
        const token = await user.getIdToken();
        useAuthStore.getState().setToken(token);
      }
    });
    return unsubscribe;
  }, []);

  // Global real-time company subscription: plan tier, feature flags, subscription status.
  // Same pattern as useGetStartedChecklist — refetch GET_COMPANY_INFO on COMPANY UPDATE events.
  // Mounted globally so company data stays current regardless of which page is active.
  const refetchCompany = useCallback(async () => {
    // Share pages manage company data via ShareAssetsProvider (includes domain field).
    // Refetching GET_COMPANY_INFO here would overwrite it without domain → breaks Layer 2 filtering.
    if (window.location.pathname.startsWith('/share/')) return;
    try {
      const result = await client.query<{ getCompanyInfo: CompanyInfo }>({
        query: GET_COMPANY_INFO,
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      });

      // INVALID_TEAM is a deliberate forceLogout below, not an outage — skip the
      // systemic report so expected user-state churn doesn't inflate error rate.
      if (result?.error && !hasInvalidTeamError(result)) {
        reportAppError({
          evt: 'app_error',
          flow: 'app-bootstrap',
          kind: 'systemic',
          code: 'bootstrap_partial',
        });
      }

      // Check for INVALID_TEAM error — user removed from team mid-session
      if (hasInvalidTeamError(result)) {
        await forceLogout();
        return;
      }

      if (result.data?.getCompanyInfo) {
        setCompany(result.data.getCompanyInfo);
      }
    } catch (err) {
      // Silently fail — company info will sync on next focus/visibility
      reportAppError({
        evt: 'app_error',
        flow: 'app-bootstrap',
        kind: 'systemic',
        code: 'company_refetch_failed',
        error: err,
      });
    }
  }, [client, setCompany, hasInvalidTeamError, forceLogout]);

  useRealtimeSubscription('company', refetchCompany, { refetchOnUpdate: true });

  // Global real-time user subscription: detect when user is removed from team mid-session,
  // and detect pending invitations. When a USER event is received, refetch ME_QUERY.
  // If ME_QUERY returns INVALID_TEAM or empty teams, force logout.
  const refetchMe = useCallback(async () => {
    try {
      const result = await client.query<MeQueryResult>({
        query: ME_QUERY,
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      });

      // INVALID_TEAM is a deliberate forceLogout below, not an outage — skip the
      // systemic report so expected user-state churn doesn't inflate error rate.
      if (result?.error && !hasInvalidTeamError(result)) {
        reportAppError({
          evt: 'app_error',
          flow: 'app-bootstrap',
          kind: 'systemic',
          code: 'bootstrap_partial',
        });
      }

      // Check for INVALID_TEAM error — user removed from team mid-session
      if (hasInvalidTeamError(result)) {
        await forceLogout();
        return;
      }

      // Check for empty teams array — user removed from all teams mid-session
      // Backend returns teams: [] instead of INVALID_TEAM for me query
      if (hasNoTeams(result.data?.me)) {
        await forceLogout();
        return;
      }

      if (result.data?.me) {
        setMe(result.data.me);
        setTeams(result.data.me.teams ?? []);
        // Sync pending invitation state for InvitationAcceptanceDialog
        if (result.data.me.inviteUser) {
          useAuthStore.getState().setInviteUser(result.data.me.inviteUser);
        }
      }
    } catch (err) {
      // Silently fail — ME will sync on next focus/visibility
      reportAppError({
        evt: 'app_error',
        flow: 'app-bootstrap',
        kind: 'systemic',
        code: 'me_refetch_failed',
        error: err,
      });
    }
  }, [client, setMe, setTeams, hasInvalidTeamError, hasNoTeams, forceLogout]);

  // Detect user changes in real-time: team removal, invitation, role changes.
  // Listens via 'realtime:event' CustomEvent (dispatched by useRealtimeSubscription)
  // instead of a separate useRealtimeSubscription('user', ...) call — Apollo Client
  // deduplicates useSubscription hooks with identical query+variables, so the 'user'
  // instance's onData never fires. The DOM event bypasses this limitation.
  const lastUserEventRef = useRef(0);
  const lastCompanyEventRef = useRef(0);
  useEffect(() => {
    const EVENT_DEBOUNCE_MS = 2_000;

    const handleRealtimeEvent = (e: Event) => {
      const event = (e as CustomEvent).detail;
      const type = event?.type?.toLowerCase();
      // Role DELETE also requires refetchMe (deleted role affects user's permissions)
      const isRoleDelete = type === 'role' && event?.mutation === 'DELETE';
      // Team ADD/UPDATE/DELETE all need refetchMe to sync TeamSwitcher dropdown
      const isTeamEvent = type === 'team';
      if (event?.mutation !== 'UPDATE' && !isRoleDelete && !isTeamEvent) return;

      const now = Date.now();

      // Debounce: multiple useRealtimeSubscription instances each dispatch the CustomEvent,
      // so this handler fires N times per single WS event. One refetch is enough.
      if (type === 'user' || type === 'role' || type === 'team') {
        if (now - lastUserEventRef.current < EVENT_DEBOUNCE_MS) return;
        lastUserEventRef.current = now;
        refetchMe();
      } else if (type === 'company') {
        if (now - lastCompanyEventRef.current < EVENT_DEBOUNCE_MS) return;
        lastCompanyEventRef.current = now;
        refetchCompany();
        // `me` carries accountStatus (the inactive-account paywall) and payment.dunning
        // (the past-due banner). Without this, an out-of-band reactivation — staff Force
        // Refresh, or a dunning cure after paying via the emailed Stripe link — leaves the
        // open tab behind the paywall until a reload.
        if (!window.location.pathname.startsWith('/share/')) refetchMe();
      }
    };

    window.addEventListener('realtime:event', handleRealtimeEvent);
    return () => window.removeEventListener('realtime:event', handleRealtimeEvent);
  }, [refetchMe, refetchCompany]);

  // Cross-tab sync: refetch ME_QUERY on window focus/visibility.
  // Uses blur→focus guard: only refetch when focus truly left the browser window,
  // not on in-app interactions (folder clicks, menus, popovers).
  // Company data is handled by the WS subscription above — focus only syncs ME_QUERY.
  const lastRefetchRef = useRef(0);
  const windowBlurredRef = useRef(false);
  useEffect(() => {
    const FOCUS_DEBOUNCE_MS = 2_000;

    const handleBlur = () => {
      windowBlurredRef.current = true;
    };

    const handleFocus = async () => {
      if (!windowBlurredRef.current) return; // in-app interaction, skip
      windowBlurredRef.current = false;

      // Share pages manage their own auth/company via ShareAssetsProvider.
      // Refetching ME_QUERY + GET_COMPANY_INFO would overwrite company (loses domain field).
      if (window.location.pathname.startsWith('/share/')) return;

      const now = Date.now();
      if (now - lastRefetchRef.current > FOCUS_DEBOUNCE_MS) {
        lastRefetchRef.current = now;
        if (!useAuthStore.getState().token) return;

        // SAML sliding window: validate TTL, refresh on valid, sign out on expired
        if (auth.currentUser && checkSamlSessionExpired(auth.currentUser.uid)) {
          await forceLogout();
          return;
        }

        Promise.allSettled([
          client.query<MeQueryResult>({
            query: ME_QUERY,
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          }),
          client.query<GetCompanyInfoQueryResult>({
            query: GET_COMPANY_INFO,
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          }),
        ]).then(async ([meResult, companyResult]) => {
          // INVALID_TEAM is a deliberate forceLogout below, not an outage — skip the
          // systemic report so expected user-state churn doesn't inflate error rate.
          if (
            meResult.status === 'fulfilled' &&
            meResult.value.error &&
            !hasInvalidTeamError(meResult.value)
          ) {
            reportAppError({
              evt: 'app_error',
              flow: 'app-bootstrap',
              kind: 'systemic',
              code: 'bootstrap_partial',
            });
          }
          if (
            companyResult.status === 'fulfilled' &&
            companyResult.value.error &&
            !hasInvalidTeamError(companyResult.value)
          ) {
            reportAppError({
              evt: 'app_error',
              flow: 'app-bootstrap',
              kind: 'systemic',
              code: 'bootstrap_partial',
            });
          }
          // Check for INVALID_TEAM error — user removed from team mid-session
          if (meResult.status === 'fulfilled' && hasInvalidTeamError(meResult.value)) {
            await forceLogout();
            return;
          }
          if (companyResult.status === 'fulfilled' && hasInvalidTeamError(companyResult.value)) {
            await forceLogout();
            return;
          }
          // Check for empty teams array — user removed from all teams mid-session
          if (meResult.status === 'fulfilled' && hasNoTeams(meResult.value.data?.me)) {
            await forceLogout();
            return;
          }
          if (meResult.status === 'fulfilled' && meResult.value.data?.me) {
            const meData = meResult.value.data.me;
            setMe(meData);
            setTeams(meData.teams ?? []);
            // Sync pending invitation state for InvitationAcceptanceDialog
            if (meData.inviteUser) {
              useAuthStore.getState().setInviteUser(meData.inviteUser);
            }
          }
          if (companyResult.status === 'fulfilled' && companyResult.value.data?.getCompanyInfo) {
            setCompany(companyResult.value.data.getCompanyInfo);
          }
        });
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        windowBlurredRef.current = false;
        handleFocus();
      }
    };

    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [client, setMe, setCompany, setTeams, hasInvalidTeamError, hasNoTeams, forceLogout]);

  // Listen for INACTIVE_ACCOUNT errors from Apollo error link.
  // When any query fails with this error, re-fetch ME_QUERY to confirm and update store.
  // This provides instant detection when the account becomes inactive mid-session.
  // Debounced: multiple simultaneous failed queries each dispatch the event, but we only
  // need one ME_QUERY refetch.
  const lastInactiveCheckRef = useRef(0);
  useEffect(() => {
    const INACTIVE_DEBOUNCE_MS = 2_000;

    const handleAccountInactive = () => {
      const now = Date.now();
      if (now - lastInactiveCheckRef.current < INACTIVE_DEBOUNCE_MS) return;
      lastInactiveCheckRef.current = now;

      client
        .query<MeQueryResult>({
          query: ME_QUERY,
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        })
        .then(async (result) => {
          // INVALID_TEAM is a deliberate forceLogout below, not an outage — skip the
          // systemic report so expected user-state churn doesn't inflate error rate.
          if (result?.error && !hasInvalidTeamError(result)) {
            reportAppError({
              evt: 'app_error',
              flow: 'app-bootstrap',
              kind: 'systemic',
              code: 'bootstrap_partial',
            });
          }
          // Check for INVALID_TEAM error — user removed from team mid-session
          if (hasInvalidTeamError(result)) {
            await forceLogout();
            return;
          }
          // Check for empty teams — user removed from all teams
          if (hasNoTeams(result.data?.me)) {
            await forceLogout();
            return;
          }
          if (result.data?.me) {
            const meData = result.data.me;
            setMe(meData);
            setTeams(meData.teams ?? []);
            if (meData.inviteUser) {
              useAuthStore.getState().setInviteUser(meData.inviteUser);
            }
          }
        });
    };

    window.addEventListener('auth:account-inactive', handleAccountInactive);
    return () => window.removeEventListener('auth:account-inactive', handleAccountInactive);
  }, [client, setMe, setTeams, hasInvalidTeamError, hasNoTeams, forceLogout]);

  // Listen for UNAUTHENTICATED / API_TOKEN_INVALID / API_TOKEN_EXPIRED errors from Apollo error link.
  // These fire when the Firebase token is revoked (e.g. user deleted from account) or invalid.
  // Force immediate logout — no need to re-query, the token is already rejected by the backend.
  const lastTokenExpiredCheckRef = useRef(0);
  useEffect(() => {
    const TOKEN_EXPIRED_DEBOUNCE_MS = 2_000;

    const handleTokenExpired = async () => {
      // Skip logout on share link pages — public queries don't need auth
      if (window.location.pathname.startsWith('/share/')) return;

      const now = Date.now();
      if (now - lastTokenExpiredCheckRef.current < TOKEN_EXPIRED_DEBOUNCE_MS) return;
      lastTokenExpiredCheckRef.current = now;

      // Only act if we're fully logged in — avoid spurious logouts during initial auth load.
      // During app startup, eager queries (e.g. getAppCfgs from GetStartedDropdown) may fire
      // before Firebase populates auth.currentUser from IndexedDB, causing UNAUTHENTICATED errors.
      // These are transient and resolve once onAuthStateChanged fires — don't log out for them.
      const state = useAuthStore.getState();
      if (state.token && !state.loading && !state.signupInProgress) {
        await forceLogout();
      }
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => window.removeEventListener('auth:token-expired', handleTokenExpired);
  }, [forceLogout]);

  // Load user context after auth
  const token = useAuthStore((s) => s.token);
  const loadUserContextRetry = useAuthStore((s) => s.loadUserContextRetry);

  useEffect(() => {
    if (!token) return;

    // Check if error contains NOT_FOUND_USER (orphaned Firebase user without MongoDB user)
    // Apollo Client 4 with errorPolicy:'all' returns error in result.error
    function hasNotFoundUserError(result: { error?: { message?: string } }): boolean {
      return result.error?.message?.includes('NOT_FOUND_USER') ?? false;
    }

    async function loadUserContext() {
      // Share link pages: only skip when the live Firebase user is anonymous —
      // those users have no MongoDB user record and ME_QUERY would force-logout.
      // For REAL signed-in users on /share/* (e.g. ACCOUNT-type "Your account
      // members" share), we must still load `me` so ShareAssetsProvider can detect
      // same-company vs cross-company. Without this, authState.me stays null →
      // fake me is injected → useSmallAppMicroService mistakes the user for
      // cross-company → mints a secondary anonymous Firebase token (no claims) →
      // SmallApp REST `getMe()` returns null → 403 "Invalid access token".
      // GET_COMPANY_INFO is intentionally still suppressed on share pages
      // (ShareAssetsProvider provides company data via getSharedCompany).
      const isShareLinkPage = window.location.pathname.startsWith('/share/');
      const skipShareLoad = isShareLinkPage && (!auth.currentUser || auth.currentUser.isAnonymous);
      if (skipShareLoad) return;

      // During signup, skip ME+Company queries — they'll fail with NOT_FOUND_USER
      // because register mutation hasn't run yet. SignupPage will re-trigger via
      // incrementLoadUserContextRetry() after registration completes.
      if (useAuthStore.getState().signupInProgress) return;

      // Run ME_QUERY and GET_COMPANY_INFO in parallel for faster load
      // ME is critical (sets user + teams); company is needed for branding/features
      const [meSettled, companySettled] = await Promise.allSettled([
        client.query<MeQueryResult>({
          query: ME_QUERY,
          fetchPolicy: 'network-only',
          errorPolicy: 'all', // accept partial data (e.g. planFeatures null)
        }),
        client.query<GetCompanyInfoQueryResult>({
          query: GET_COMPANY_INFO,
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        }),
      ]);

      // Handle ME_QUERY result
      let meData = null;
      if (meSettled.status === 'fulfilled') {
        const meResult = meSettled.value;

        // INVALID_TEAM and NOT_FOUND_USER are both deliberate user-state outcomes
        // handled below (forceLogout / orphan recovery), not an outage — skip the
        // systemic report for either so expected churn doesn't inflate error rate.
        if (meResult.error && !hasInvalidTeamError(meResult) && !hasNotFoundUserError(meResult)) {
          reportAppError({
            evt: 'app_error',
            flow: 'app-bootstrap',
            kind: 'systemic',
            code: 'bootstrap_partial',
          });
        }

        // INVALID_TEAM: user exists in MongoDB but belongs to no teams.
        if (hasInvalidTeamError(meResult)) {
          await forceLogout();
          return;
        }

        // NOT_FOUND_USER: Firebase user exists but no MongoDB user.
        // LoginPage/SignupPage handle orphan recovery themselves (reCAPTCHA + REGISTER_USER).
        // If we get here, just sign out so the user lands back on the login page.
        if (hasNotFoundUserError(meResult) && !meResult.data?.me) {
          if (!useAuthStore.getState().signupInProgress) {
            await forceLogout();
            return;
          }
        } else {
          meData = meResult.data?.me;
        }
      } else {
        reportAppError({
          evt: 'app_error',
          flow: 'app-bootstrap',
          kind: 'systemic',
          code: 'me_load_failed',
          error: meSettled.reason,
        });
      }

      // Check for empty teams array — user removed from all teams
      // Backend returns teams: [] instead of INVALID_TEAM for me query
      if (hasNoTeams(meData)) {
        await forceLogout();
        return;
      }

      if (meData) {
        setMe(meData);
        setTeams(meData.teams ?? []);

        // Dual-live team sync: when switching from scio1→scio3, scio1 passes ?activeTeamId=
        // so scio3 can sync to the same team the user was on in scio1.
        // activeTeamId is separate from teamId — teamId is folder-level context for
        // cross-team shared folders and must stay in the URL for folder queries (#459).
        const urlParams = new URLSearchParams(window.location.search);
        const urlActiveTeamId = urlParams.get('activeTeamId');
        if (urlActiveTeamId && meData.teams?.some((t) => t._id === urlActiveTeamId)) {
          useTeamStore.getState().setActiveTeam(urlActiveTeamId);
          // scio1 is the authoritative source for the landing URL — drop any cached
          // folder positions so usePersistedPath doesn't restore a stale folder when
          // the URL has no ?path= (e.g. user was on Engage Home in scio1) (#348).
          clearPersistedPaths();
          urlParams.delete('activeTeamId');
          const cleanUrl = urlParams.toString()
            ? `${window.location.pathname}?${urlParams}`
            : window.location.pathname;
          window.history.replaceState({}, '', cleanUrl);
        }

        // Group Security v3 (§12, D8) — login home-land. On a fresh sign-in, if the user's group defines
        // a home teamspace, switch the active team to it, then mark a one-time "pending home land" that
        // the first content page consumes to navigate into `homeFolder` (AuthProvider sits above the
        // router and can't navigate itself). A scio1 handoff (?activeTeamId=) always wins, so we skip.
        //
        // The guard MUST persist across full page reloads (sessionStorage, keyed by uid) — TeamSwitcher
        // reloads the page with no ?activeTeamId, and a per-mount ref would let the home-team switch
        // re-fire on that reload and bounce the user back to their home team (can't switch away).
        if (homeLandUserRef.current !== meData._id && !hasHomeLandedThisSession(meData._id)) {
          homeLandUserRef.current = meData._id;
          markHomeLandedThisSession(meData._id);
          const homeTeamId = meData.homeTeamId;
          const switchHome =
            !urlActiveTeamId && !!homeTeamId && !!meData.teams?.some((tm) => tm._id === homeTeamId);
          if (switchHome && homeTeamId) {
            useTeamStore.getState().setActiveTeam(homeTeamId);
          }
          // Only land in the home folder when we'll be sitting on its team (it defines no specific
          // team, or we just switched / already are on it) and scio1 didn't force a different team.
          const onHomeTeam =
            !homeTeamId || switchHome || useTeamStore.getState().activeTeamId === homeTeamId;
          if (meData.homeFolders?.length && onHomeTeam && !urlActiveTeamId) {
            // §12 per-type: mark EACH home type pending so every content page lands at its own home
            // (Screens→device, Assets→asset, …), each consumed once by its page.
            markHomeLandPending(meData.homeFolders.map((h) => h.type).filter(Boolean));
          } else {
            clearHomeLandPending();
          }
        }

        // Update cache owner — keeps IndexedDB cache key in sync with current session.
        // Must run after ?activeTeamId handling and setTeams so activeTeamId is final.
        const finalTeamId = useTeamStore.getState().activeTeamId;
        const newOwner = {
          accountId: meData.accountId,
          teamId: finalTeamId,
          uid: auth.currentUser?.uid ?? '',
        };
        const prevOwner = getCacheOwner();
        if (
          prevOwner &&
          (prevOwner.accountId !== newOwner.accountId || prevOwner.teamId !== newOwner.teamId)
        ) {
          // Account or team changed (e.g. ?activeTeamId= URL param) — clear stale cache + paths.
          // Skip on share-link pages: ShareAssetsProvider deliberately switches activeTeamId
          // to the share folder's team while FolderSharePage's getSharedObject/Assets/Company
          // queries are in flight. Calling client.clearStore() here aborts those queries with
          // status "(canceled)" → "Failed to load shared content" on first load. Reload "fixes"
          // it because the cached owner already matches the share team. Cross-user/orphan
          // detection is also disabled on share pages (see onAuthStateChanged ~line 114).
          if (!window.location.pathname.startsWith('/share/')) {
            await client.clearStore();
            await clearPersistedCache();
            clearPersistedPaths();
          }
        }
        setCacheOwnerStorage(newOwner);

        // Set pending invitation for InvitationAcceptanceDialog
        if (meData.inviteUser) {
          useAuthStore.getState().setInviteUser(meData.inviteUser);
        }
        // Track login analytics (once per session). Distinguish a FRESH
        // sign-in from a session RESUME using Firebase's `lastSignInTime`
        // metadata — if the user signed in within the last 30 seconds we
        // treat it as a fresh login (fire `analyticsLogin` → GTM + PostHog
        // `user_logged_in` event). Otherwise the page is reloading on an
        // already-authenticated session, so we fire `analyticsLoginResume`
        // → GTM `loginEvent` only (no duplicate PostHog event). Mirrors
        // scio1's split between `trackingLogIn` (authUser.js:337, fresh)
        // and `onlyTrackingLogInResume` (dashboard.js:199, every mount).
        if (!loginTracked.current) {
          loginTracked.current = true;
          const firebaseUser = useAuthStore.getState().firebaseUser;
          const firebaseProvider = firebaseUser?.providerData?.[0]?.providerId;
          const lastSignInRaw = firebaseUser?.metadata?.lastSignInTime;
          const lastSignInMs = lastSignInRaw ? new Date(lastSignInRaw).getTime() : 0;
          const isFreshSignIn = lastSignInMs > 0 && Date.now() - lastSignInMs < 30_000;
          if (isFreshSignIn) {
            analyticsLogin({ method: firebaseProvider ?? 'email', isMfa: false });
          } else {
            analyticsLoginResume();
          }
        }
      }

      // Handle GET_COMPANY_INFO result
      let companyScioVersion: number | undefined;
      if (companySettled.status === 'fulfilled') {
        const companyResult = companySettled.value;

        // Same two user-state conditions as the ME_QUERY branch above (both predicates
        // are in scope here too — this branch runs inside the same effect) — skip the
        // systemic report when either explains the failure.
        if (
          companyResult.error &&
          !hasInvalidTeamError(companyResult) &&
          !hasNotFoundUserError(companyResult)
        ) {
          reportAppError({
            evt: 'app_error',
            flow: 'app-bootstrap',
            kind: 'systemic',
            code: 'bootstrap_partial',
          });
        }

        // Check for INVALID_TEAM error — user removed from team or doesn't belong to any team
        if (hasInvalidTeamError(companyResult)) {
          await forceLogout();
          return;
        }

        if (companyResult.data?.getCompanyInfo) {
          setCompany(companyResult.data.getCompanyInfo);
          const raw = companyResult.data.getCompanyInfo.scioVersion;
          companyScioVersion = raw != null ? Number(raw) : undefined;

          // trialCreated GTM event (scio1 parity — dashboard.js:170). Fire
          // once per account while the trial flag is still NOT_TRIGGERED, then
          // flip it via `triggerTrialStartEvent` so it doesn't fire again on the
          // next page load. NOTE: the backend stores the lowercase enum VALUE
          // ('not_triggered'), not the uppercase key — compare against the
          // shared NOT_TRIGGERED constant, never the literal 'NOT_TRIGGERED'
          // (that bug silently disabled this event for all scio3 signups).
          // Wrapped in try/catch via safeTrack; the mutation is fire-and-forget.
          if (
            !trialEventTracked.current &&
            companyResult.data.getCompanyInfo.payment?.trialStartEventFired === NOT_TRIGGERED
          ) {
            trialEventTracked.current = true;
            analyticsTrialCreated();
            client
              .mutate({ mutation: TRIGGER_TRIAL_START_EVENT })
              .catch((err) => console.warn('[analytics] triggerTrialStartEvent failed', err));
          }
        }
      } else {
        reportAppError({
          evt: 'app_error',
          flow: 'app-bootstrap',
          kind: 'systemic',
          code: 'company_load_failed',
          error: companySettled.reason,
        });
      }
      // Mark company query as settled (success or failure) so plan-gated pages can render
      setCompanyLoaded();

      // Identify user across PostHog + Matomo
      if (meData) {
        const activeTeamId = useTeamStore.getState().activeTeamId;
        const companyData =
          companySettled.status === 'fulfilled' ? companySettled.value.data?.getCompanyInfo : null;

        analyticsIdentify({
          userId: meData._id,
          email: meData.email,
          firstName: meData.firstName,
          lastName: meData.lastName,
          rootRole: meData.rootRole,
          accountId: meData.accountId,
          teamId: activeTeamId ?? undefined,
          plan: companyData?.payment?.plan,
          isTrial: companyData?.payment?.isTrial ?? false,
          screenCount: companyData?.noOfScreens,
          companyName: companyData?.name,
          companyScioVersion,
          // Generic signup cohort properties for analytics + PostHog flag
          // targeting (e.g. gating a flag to users created after a launch date).
          // Always 'scio3' here — smallapps/sites would set their own source if
          // they ever identify.
          signupDate: companyData?.createdAt,
          signupSource: 'scio3',
          // Account-level signup date (company.createdAt), distinct from the
          // per-user signup_date above.
          companySignupDate: companyData?.createdAt,
        });
      }

      // Dual-live routing cookie for CF Worker (scio1/scio3 switch)
      // See: lib/sso-cookies.ts handleDualLiveRouting() for full logic
      console.warn(
        '[scio3] scioVersion — user:',
        meData?.scioVersion,
        '| company:',
        companyScioVersion,
      );
      if (!isInIframeContext && meData) {
        // Skip dual-live routing for SAML sessions — scio1 cannot resume a SAML
        // Firebase session via ?scio3_auth=1, so keep the user on scio3 (#709)
        const isSamlSession = useAuthStore.getState().signInProvider?.startsWith('saml.');
        // Skip dual-live routing on share-link pages. The user's `me` is augmented
        // with the SHARE OWNER's accountId/scioVersion (cross-team / cross-company
        // path via `_augmentWithShareLinkContext`), and `getCompanyInfo` returns
        // the share OWNER's company — both reflect the share's host, not the
        // signed-in user's home company. Letting `handleDualLiveRouting` run here
        // would force-redirect to scio1 whenever the share owner is on scio1
        // (legacy), flipping the `__scio_ver` cookie globally and dragging every
        // OTHER tab the same browser has open into scio1 → instant cross-tab
        // logout for the user's actual session. Only safe to evaluate routing on
        // the user's own pages where me/companyInfo describe the user.
        const isShareLinkPage = window.location.pathname.startsWith('/share/');
        // Skip dual-live routing on the OptiDev popup sign-in page (/signIn/optidev).
        // It's a transient popup whose only job is to authenticate and postMessage the
        // Firebase token back to the OptiDev opener. Redirecting it to the account's
        // canonical scio version (e.g. scio1) would navigate the popup away and abandon
        // the handoff — the popup would close without ever returning a token. Same
        // "not the user's home page" rationale as the share-link exclusion above.
        const isOptiDevSignInPage = window.location.pathname.startsWith('/signIn/optidev');
        if (!isSamlSession && !isShareLinkPage && !isOptiDevSignInPage) {
          const redirected = handleDualLiveRouting(companyScioVersion, meData.scioVersion);
          if (redirected) return;
        }
      }

      // NOTE: Notifications are handled via Meteor in scio1, not GraphQL.
      // The GraphQL API doesn't have a notifications query.
      // Notification loading will be implemented when the backend adds support.

      // During signup, ME_QUERY returns NOT_FOUND_USER (no MongoDB user yet).
      // Don't set loading=false — SignupPage handles its own loading state.
      // Without this guard, the app renders with stale/empty stores before signup completes.
      if (useAuthStore.getState().signupInProgress && !meData) return;

      // User context loaded (or failed) — allow app to render
      setLoading(false);
    }

    loadUserContext();
  }, [
    token,
    loadUserContextRetry,
    client,
    setMe,
    setCompany,
    setCompanyLoaded,
    setTeams,
    setLoading,
    hasInvalidTeamError,
    hasNoTeams,
    forceLogout,
  ]);

  return <>{children}</>;
}
